import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play, Settings, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { parseChordPro, CHORD_KEYS, semitoneDistance, transposeChord, transposeLine } from '@/chordpro/index';
import { ChordAnchoredLine, ChordRow, isChordOnly } from './ChordLines';

const SPEEDS = { langzaam: 10, normaal: 20, snel: 36 };
export const ZOOM_STEPS = [80, 90, 100, 110, 125, 140, 160];
const DEFAULT_ZOOM_INDEX = ZOOM_STEPS.indexOf(100);
export const CAPO_MAX = 12;
export const BPM_MIN = 30;
export const BPM_MAX = 300;
const BPM_START = 100;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// Persoonlijke leesinstellingen per lied (capo, tempo) blijven alleen in de browser van de lezer: de publieke setlist heeft
// geen inlog en de oorspronkelijke liedgegevens en de centrale setlist veranderen er nooit door.
const storageKey = (id) => `zoea:public-reader:${id}`;
const loadPersonal = (id) => {
  if (!id) return {};
  try {
    const stored = JSON.parse(window.localStorage.getItem(storageKey(id)) || '{}');
    return {
      capo: Number.isInteger(stored.capo) ? clamp(stored.capo, 0, CAPO_MAX) : 0,
      bpm: Number.isFinite(stored.bpm) ? clamp(Math.round(stored.bpm), BPM_MIN, BPM_MAX) : null,
    };
  } catch (error) {
    return {};
  }
};
const savePersonal = (id, values) => {
  if (!id) return;
  try {
    window.localStorage.setItem(storageKey(id), JSON.stringify(values));
  } catch (error) {
    // opslag geblokkeerd (privévenster): de instelling geldt dan alleen tijdens dit bezoek
  }
};

// Met een capo speel je andere vormen dan je hoort. Bij een platte toonsoort (F, Bb, Eb, Ab, Db, Gb en hun mineur) staan de
// vormen met mollen, anders met kruisen: capo 2 op C speelt Bb-vormen, capo 3 op C speelt A-vormen.
const FLAT_SHAPES = new Set(['F', 'Bb', 'Eb', 'Ab', 'Db', 'Gb', 'Dm', 'Gm', 'Cm', 'Fm', 'Bbm', 'Ebm']);
export const capoShape = (key, capo) => {
  if (!capo) return { key, preferFlats: key.includes('b') };
  const flatName = transposeChord(key, -capo, true);
  const preferFlats = FLAT_SHAPES.has(flatName);
  return { key: transposeChord(key, -capo, preferFlats), preferFlats };
};

const PublicReaderLine = ({ line, semitones, preferFlats, chordPosition, displayMode }) => {
  if (line.isAnnotation) {
    return <p className="public-reader-annotation">{line.raw}</p>;
  }
  if (!line.raw.trim()) return <div className="public-reader-spacer" aria-hidden="true" />;
  const rendered = transposeLine(line, semitones, preferFlats);
  if (displayMode === 'lyrics') {
    return (
      <div className="public-reader-line public-reader-line-text">
        {rendered.segments.map((part) => part.text).join('') || ' '}
      </div>
    );
  }
  if (chordPosition === 'inline') {
    return (
      <div className="public-reader-line public-reader-line-inline">
        {rendered.segments.map((part, index) => (
          <span key={index} className="public-reader-inline-segment">
            {part.chord && <strong>{part.chord}</strong>}
            <span>{part.text || ' '}</span>
          </span>
        ))}
      </div>
    );
  }
  // Akkoorden zijn nulbrede ankers in de doorlopende tekst: de songtekst staat altijd op dezelfde plek, ook bij lange akkoorden.
  return isChordOnly(rendered)
    ? <ChordRow line={rendered} className="public-reader-line public-reader-chord-row" />
    : <ChordAnchoredLine line={rendered} className="public-reader-line public-reader-anchored" />;
};

const PublicReaderSection = ({ section, semitones, preferFlats, chordPosition, displayMode }) => {
  const showHeader = section.type !== 'body';
  return (
    <section className="public-reader-section">
      {showHeader && <h3>{section.label || section.type}</h3>}
      {section.lines.map((line, lineIndex) => (
        <PublicReaderLine key={lineIndex} line={line} semitones={semitones} preferFlats={preferFlats} chordPosition={chordPosition} displayMode={displayMode} />
      ))}
    </section>
  );
};

/**
 * Fullscreen, read-only song presentation for the anonymous public setlist
 * share flow. Deliberately does not reuse ChordProRenderer's markup/toolbar
 * (that component stays desktop/internal-tool shaped) but shares its parsing
 * and transposition utilities so chord math never diverges between the two.
 */
const PublicSongReader = ({
  item,
  song,
  index,
  total,
  onBack,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
}) => {
  const source = song ? (song.chordpro || `{title: ${song.title}}\n\n${song.lyrics || ''}`) : '';
  const parsed = useMemo(() => parseChordPro(source), [source]);
  const originalKey = parsed.metadata.key || item?.key || song?.default_key || 'C';
  const requestedKey = item?.key || song?.default_key || originalKey;
  const [targetKey, setTargetKey] = useState(requestedKey);
  const [chordPosition, setChordPosition] = useState('above');
  const [displayMode, setDisplayMode] = useState('chords');
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [speed, setSpeed] = useState('normaal');
  const [scrolling, setScrolling] = useState(false);
  const viewportRef = useRef(null);
  const personalId = song?.id || item?.id;
  const [capo, setCapo] = useState(0);
  const [personalBpm, setPersonalBpm] = useState(null);
  const semitones = semitoneDistance(originalKey, targetKey);
  const shape = capoShape(targetKey, capo);
  const preferFlats = capo ? shape.preferFlats : targetKey.includes('b');
  const title = song?.title || item?.title || 'Lied';
  const originalBpm = item?.bpm || song?.bpm || null;
  const bpm = personalBpm ?? originalBpm;

  useEffect(() => setTargetKey(requestedKey), [requestedKey]);
  // Capo en tempo zijn persoonlijk en per lied: bij een ander lied de bewaarde waarden van dat lied laden.
  useEffect(() => {
    const stored = loadPersonal(personalId);
    setCapo(stored.capo || 0);
    setPersonalBpm(stored.bpm ?? null);
  }, [personalId]);
  const updatePersonal = (nextCapo, nextBpm) => {
    setCapo(nextCapo);
    setPersonalBpm(nextBpm);
    savePersonal(personalId, { capo: nextCapo, bpm: nextBpm });
  };
  const stepBpm = (delta) => updatePersonal(capo, clamp((personalBpm ?? originalBpm ?? BPM_START) + delta, BPM_MIN, BPM_MAX));
  useEffect(() => {
    if (!settingsOpen) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setSettingsOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [settingsOpen]);
  useEffect(() => {
    setScrolling(false);
    if (viewportRef.current) viewportRef.current.scrollTop = 0;
  }, [item?.id]);

  useEffect(() => {
    if (!scrolling) return undefined;
    let last = performance.now();
    let frame;
    const tick = (now) => {
      const viewport = viewportRef.current;
      if (!viewport) return;
      viewport.scrollTop += SPEEDS[speed] * ((now - last) / 1000);
      last = now;
      if (viewport.scrollTop + viewport.clientHeight >= viewport.scrollHeight - 2) setScrolling(false);
      else frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [scrolling, speed]);

  const zoom = ZOOM_STEPS[zoomIndex];
  const pdfLinkUrl = item?.pdf_link_url || song?.pdf_link_url;
  const pdfLinkLabel = item?.pdf_link_label || song?.pdf_link_label;

  return (
    <div
      className="public-reader"
      data-testid="public-song-reader"
      data-zoom={zoom}
      data-display-mode={displayMode}
      style={{ '--reader-scale': zoom / 100 }}
    >
      <header className="public-reader-topbar">
        <button type="button" className="public-reader-back" onClick={onBack} data-testid="public-reader-back">
          <ChevronLeft className="h-5 w-5" />
          <span>Setlist</span>
        </button>
        <p className="public-reader-title" title={title}>{title}</p>
        <button
          type="button"
          className="public-reader-settings-trigger"
          aria-label="Instellingen"
          aria-haspopup="dialog"
          aria-expanded={settingsOpen}
          onClick={() => setSettingsOpen((value) => !value)}
        >
          <Settings className="h-5 w-5" />
        </button>
      </header>

      <div ref={viewportRef} className="public-reader-viewport">
        <div className="public-reader-song-header">
          <h1>{title}</h1>
          <p className="public-reader-meta">
            {[targetKey && `Toonsoort ${targetKey}`, capo > 0 && displayMode === 'chords' && `Capo ${capo} · speel in ${shape.key}`, bpm && `${bpm} BPM`].filter(Boolean).join(' · ')}
          </p>
        </div>

        {song && parsed.sections.map((section, sectionIndex) => (
          <PublicReaderSection
            key={`${section.type}-${sectionIndex}`}
            section={section}
            semitones={semitones - capo}
            preferFlats={preferFlats}
            chordPosition={chordPosition}
            displayMode={displayMode}
          />
        ))}
        {song && !parsed.sections.length && (
          <p className="public-reader-empty">Nog geen liedtekst beschikbaar.</p>
        )}
        {!song && pdfLinkUrl && (
          <div className="public-reader-pdf-only">
            <p>Voor dit onderdeel is alleen een PDF-link beschikbaar.</p>
            <a href={pdfLinkUrl} target="_blank" rel="noreferrer">{pdfLinkLabel || 'Open PDF'}</a>
          </div>
        )}
      </div>

      <nav className="public-reader-bottombar">
        <button type="button" className="public-reader-nav-btn" onClick={onPrev} disabled={!hasPrev} data-testid="public-reader-prev">
          <ChevronLeft className="h-4 w-4" />
          Vorige
        </button>
        <span className="public-reader-position">Lied {index + 1} van {total}</span>
        <button type="button" className="public-reader-nav-btn" onClick={onNext} disabled={!hasNext} data-testid="public-reader-next">
          Volgende
          <ChevronRight className="h-4 w-4" />
        </button>
      </nav>

      {settingsOpen && (
        <>
          <div className="public-reader-backdrop" data-testid="public-reader-backdrop" onClick={() => setSettingsOpen(false)} />
          <div className="public-reader-settings-sheet" role="dialog" aria-label="Leesinstellingen" data-testid="public-reader-settings">
            <div className="public-reader-sheet-header">
              <span>Instellingen</span>
              <button type="button" aria-label="Sluiten" className="public-reader-settings-trigger" onClick={() => setSettingsOpen(false)}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="public-reader-sheet-body">
              <div className="public-reader-setting-row">
                <span>Weergave</span>
                <div className="public-reader-toggle-group">
                  <Button type="button" size="sm" variant={displayMode === 'chords' ? 'default' : 'outline'} aria-pressed={displayMode === 'chords'} onClick={() => setDisplayMode('chords')}>Akkoorden + tekst</Button>
                  <Button type="button" size="sm" variant={displayMode === 'lyrics' ? 'default' : 'outline'} aria-pressed={displayMode === 'lyrics'} onClick={() => setDisplayMode('lyrics')}>Alleen tekst</Button>
                </div>
              </div>
              <div className="public-reader-setting-row">
                <span>Tekstgrootte</span>
                <div className="public-reader-toggle-group">
                  <Button type="button" size="sm" variant="outline" aria-label="Kleiner" disabled={zoomIndex === 0} onClick={() => setZoomIndex((value) => Math.max(0, value - 1))}>A-</Button>
                  <button type="button" className="public-reader-zoom-value" data-testid="public-reader-zoom-value" title="Terug naar 100%" onClick={() => setZoomIndex(DEFAULT_ZOOM_INDEX)}>{zoom}%</button>
                  <Button type="button" size="sm" variant="outline" aria-label="Groter" disabled={zoomIndex === ZOOM_STEPS.length - 1} onClick={() => setZoomIndex((value) => Math.min(ZOOM_STEPS.length - 1, value + 1))}>A+</Button>
                </div>
              </div>
              <label className="public-reader-setting-row">
                <span>Toonsoort</span>
                <select value={targetKey} onChange={(event) => setTargetKey(event.target.value)}>
                  {CHORD_KEYS.map((key) => <option key={key}>{key}</option>)}
                </select>
              </label>
              {displayMode === 'chords' && (
                <div className="public-reader-setting-row">
                  <span>Capo</span>
                  <div className="public-reader-toggle-group">
                    <Button type="button" size="sm" variant="outline" aria-label="Capo lager" disabled={capo === 0} onClick={() => updatePersonal(Math.max(0, capo - 1), personalBpm)}>−</Button>
                    <span className="public-reader-zoom-value" data-testid="public-reader-capo-value">{capo === 0 ? 'Geen' : `Fret ${capo}`}</span>
                    <Button type="button" size="sm" variant="outline" aria-label="Capo hoger" disabled={capo === CAPO_MAX} onClick={() => updatePersonal(Math.min(CAPO_MAX, capo + 1), personalBpm)}>+</Button>
                  </div>
                </div>
              )}
              <div className="public-reader-setting-row">
                <span>Tempo</span>
                <div className="public-reader-toggle-group">
                  <Button type="button" size="sm" variant="outline" aria-label="Tempo 5 lager" disabled={(bpm ?? BPM_START) <= BPM_MIN} onClick={() => stepBpm(-5)}>−5</Button>
                  <Button type="button" size="sm" variant="outline" aria-label="Tempo 1 lager" disabled={(bpm ?? BPM_START) <= BPM_MIN} onClick={() => stepBpm(-1)}>−</Button>
                  <span className="public-reader-zoom-value" data-testid="public-reader-bpm-value">{bpm ? `${bpm} BPM` : 'Niet ingesteld'}</span>
                  <Button type="button" size="sm" variant="outline" aria-label="Tempo 1 hoger" disabled={(bpm ?? BPM_START) >= BPM_MAX} onClick={() => stepBpm(1)}>+</Button>
                  <Button type="button" size="sm" variant="outline" aria-label="Tempo 5 hoger" disabled={(bpm ?? BPM_START) >= BPM_MAX} onClick={() => stepBpm(5)}>+5</Button>
                  {personalBpm !== null && <Button type="button" size="sm" variant="ghost" onClick={() => updatePersonal(capo, null)}>{originalBpm ? `Origineel (${originalBpm})` : 'Wissen'}</Button>}
                </div>
              </div>
              {displayMode === 'chords' && (
                <div className="public-reader-setting-row">
                  <span>Akkoordpositie</span>
                  <div className="public-reader-toggle-group">
                    <Button type="button" size="sm" variant={chordPosition === 'above' ? 'default' : 'outline'} onClick={() => setChordPosition('above')}>Boven</Button>
                    <Button type="button" size="sm" variant={chordPosition === 'inline' ? 'default' : 'outline'} onClick={() => setChordPosition('inline')}>Inline</Button>
                  </div>
                </div>
              )}
              <div className="public-reader-setting-row">
                <span>Autoscroll</span>
                <div className="public-reader-toggle-group">
                  <select value={speed} onChange={(event) => setSpeed(event.target.value)}>
                    {Object.keys(SPEEDS).map((value) => <option key={value} value={value}>{value[0].toUpperCase() + value.slice(1)}</option>)}
                  </select>
                  <Button type="button" size="icon" variant="outline" title={scrolling ? 'Pauze' : 'Autoscroll starten'} onClick={() => setScrolling((value) => !value)}>
                    {scrolling ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              {pdfLinkUrl && (
                <a href={pdfLinkUrl} target="_blank" rel="noreferrer" className="public-reader-pdf-link">
                  {pdfLinkLabel || 'Bekijk PDF'}
                </a>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PublicSongReader;
